import React, { useEffect, useState } from 'react';
import {
 Modal,
 ModalOverlay,
 ModalContent,
 ModalBody,
 ModalHeader,
 ModalCloseButton,
 Grid,
 GridItem,
 Stack,
 Box,
 Center,
 Text,
 Flex,
 Image,
 Button,
 IconButton,
 useToast,
} from '@chakra-ui/react';
import { FiDownload, FiTrash2 } from 'react-icons/fi';
import {
 AI,
 CSV,
 DOCX,
 EPS,
 JPG,
 PDF,
 PNG,
 PPT,
 PSD,
 SVG,
 XSL,
 XSL as XLSX,
} from '../../assets/new-images/fileIcons';
import { upload, uploadImages } from '../../assets/new-images/icons';
import { InputText } from '../Input';
import baseService from '../../services/base';
import { nomeDoArquivo } from '../../utils/nome-de-upload';
import { AttentionModal } from './Attention';

const fileIconMap = {
 AI,
 CSV,
 DOCX,
 EPS,
 JPG,
 PDF,
 PNG,
 PPT,
 PSD,
 SVG,
 XSL,
 XLSX,
};

const MAX_FILENAME_LENGTH = 74;

const truncateFileName = (name = '') =>
 name.length > MAX_FILENAME_LENGTH
  ? `${name.slice(0, MAX_FILENAME_LENGTH)}...`
  : name;

// Nome do arquivo (ícone + texto). O download saiu do nome e virou botão próprio
// na coluna Ações — as duas ações (baixar/excluir) são independentes.
function FileName({ file }) {
 // EG-73: nome possivelmente gravado como latin-1. nomeDoArquivo resolve as duas
 // chaves (filename/file_name) E decodifica, num lugar so.
 const filename = nomeDoArquivo(file);
 const fileExtension = filename.split('.').pop()?.toUpperCase() || '';
 const icon = fileIconMap[fileExtension];

 return (
  <Flex h='32px' align='center' gap='12px'>
   {icon && <Image src={icon} alt={`${fileExtension} icon`} />}
   <Text
    as='span'
    fontSize='sm'
    lineHeight='19.6px'
    overflow='hidden'
    whiteSpace='nowrap'
    textOverflow='ellipsis'
   >
    {truncateFileName(filename)}
   </Text>
  </Flex>
 );
}

export default function EvidenceViewerModal({
 isOpen,
 onClose,
 control,
 currentQuestionName,
 files: propFiles,
 isAdminView = false,
}) {
 const toast = useToast();
 const [files, setFiles] = useState([]);
 // Exclusão via AttentionModal padrão com DUPLA confirmação (step 1 → step 2).
 const [del, setDel] = useState(null); // { file, step }
 const [deleting, setDeleting] = useState(false);

 // Semeia a lista local ao abrir: control._formValues não é reativo, então
 // reler no open mantém o modal em sincronia sem depender de refetch do pai.
 useEffect(() => {
  if (!isOpen) return;
  const seed =
   propFiles || control?._formValues?.[currentQuestionName]?.files || [];
  setFiles(seed);
  setDel(null);
 }, [isOpen, propFiles, control, currentQuestionName]);

 async function handleDownload(file) {
  try {
   const response = await baseService.get(
    `/question-response/download?key=${file?.file_path}`,
    { responseType: 'blob' }
   );
   const url = window.URL.createObjectURL(new Blob([response.data]));
   const link = document.createElement('a');
   link.href = url;
   // EG-73: nome legivel tambem no disco. `file_path` acima segue CRU (chave do S3).
   link.setAttribute('download', nomeDoArquivo(file, 'evidencia'));
   document.body.appendChild(link);
   link.click();
   link.remove();
   window.URL.revokeObjectURL(url);
  } catch (error) {
   toast({ title: 'Erro ao baixar evidência', status: 'error' });
  }
 }

 // 1º clique no lixo → step 1; "Sim" no modal → step 2; "Confirmar" → exclui.
 function confirmDelete() {
  if (!del) return;
  if (del.step === 1) {
   setDel({ file: del.file, step: 2 });
   return;
  }
  handleDelete(del.file);
 }

 async function handleDelete(file) {
  const idResponse = file?.id_question_response;
  const idFile = file?.id_question_response_files;
  if (!idResponse || !idFile) return;
  setDeleting(true);
  try {
   // Exclusão de 1 arquivo reusa o endpoint de upload com flag delete (mesmo
   // contrato do form em useFileUpload.ts): sem 'file', backend cai no ramo delete.
   const formData = new FormData();
   formData.append(
    'jsonPayload',
    JSON.stringify({ id_question_response_file: idFile, delete: true })
   );
   await baseService.post(
    `/question-response/upload/${idResponse}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
   );
   setFiles((prev) =>
    prev.filter((f) => f.id_question_response_files !== idFile)
   );
   toast({ title: 'Evidência excluída', status: 'success' });
  } catch (error) {
   toast({ title: 'Erro ao excluir evidência', status: 'error' });
  } finally {
   setDeleting(false);
   setDel(null);
  }
 }

 const delName = nomeDoArquivo(del?.file, 'esta evidência');

 return (
  <>
  <Modal isOpen={isOpen} onClose={onClose} size='6xl' zIndex={11000}>
   <ModalOverlay zIndex={11000} />
   <ModalContent
    maxW='1429px'
    minH='361px'
    p='32px 60px'
    borderRadius='20px'
    overflow='hidden'
    bg='white'
    zIndex={11000}
    containerProps={{
     zIndex: 11000,
    }}
   >
    <ModalHeader fontSize={['18px', '20px']} p='0'>
     {isAdminView ? 'Visualizar evidências' : 'Anexar evidências'}
    </ModalHeader>
    <ModalCloseButton bg='#F5F5FB' rounded='full' top='30px' right='60px' />

    <ModalBody p='32px 0 0'>
     {!isAdminView && (
      <Stack mb='64px'>
       <Flex height='64px' align='center' gap='6'>
        <Center bg='#E6F1F8' border='1px solid #F2F8FC' borderRadius='8px'>
         <Button
          w='full'
          h='64px'
          color='blue.100'
          bg='transparent'
          borderRadius='8px'
          p='16px 48px'
          display='flex'
          alignItems='center'
          justifyContent='center'
          _hover={{
           bg: 'none',
          }}
          userSelect='none'
         >
          <Image src={upload} alt='Upload icon' height='28px' mr='8px' />
          <Text as='span' fontSize='sm' color='#0073BB' fontWeight={500}>
           Clique aqui para enviar qualquer arquivo
          </Text>
         </Button>
        </Center>

        <Center w='35px'>
         <Text fontSize='sm' lineHeight='18px' color='#5C6066'>
          Ou
         </Text>
        </Center>

        <Center bg='#E6F1F8' border='1px solid #F2F8FC' borderRadius='8px'>
         <Button
          w='full'
          h='64px'
          color='blue.100'
          bg='transparent'
          borderRadius='8px'
          p='16px 45px'
          display='flex'
          alignItems='center'
          justifyContent='center'
          userSelect='none'
          _hover={{
           bg: 'none',
          }}
         >
          <Image src={uploadImages} alt='Upload icon' height='28px' mr='8px' />
          <Text as='span' fontSize='sm' color='#0073BB' fontWeight={500}>
           Clique aqui para enviar fotos/imagens
          </Text>
         </Button>
        </Center>

        <Box fontSize='sm' fontWeight={400} lineHeight='18px' color='#5C6066'>
         Arquivos suportados: <br />
         Fotos: .png ou .jpg. Largura ou altura mínima de 2000px. <br />
         Selos/logo: .png, .jpg , .pdf, .eps ou .ai <br />
         Tamanho máximo de arquivo: 15MB.
        </Box>
       </Flex>
      </Stack>
     )}

     <Grid
      templateColumns='40% 40% 20%'
      gap='10px'
      p='11px 12px'
      color='#16262E'
      fontSize='13px'
      fontWeight={500}
      bg='#DADADA'
      h='41px'
     >
      <GridItem whiteSpace='nowrap'>Descrição</GridItem>
      <GridItem whiteSpace='nowrap'>Arquivo</GridItem>
      <GridItem whiteSpace='nowrap'>Ações</GridItem>
     </Grid>

     {files?.length === 0 && (
      <Center h='64px' fontSize='sm' color='#818A8F'>
       Nenhuma evidência anexada
      </Center>
     )}

     {files?.map((file, index) => {
      const idFile = file?.id_question_response_files;
      const canDelete = !!file?.id_question_response && !!idFile;
      return (
       <Grid
        key={idFile ?? index}
        templateColumns='40% 40% 20%'
        gap='10px'
        bg='#FEFEFE'
        height='64px'
        color='#16262E'
       >
        <GridItem alignContent='center' pl='12px'>
         <InputText
          name={`${currentQuestionName}.files[${index}].description`}
          defaultValue={file.description || ''}
          w='98%'
          h='48px'
          border='1px solid #ADAFB3'
          isDisabled={true}
          isViewMode={true}
         />
        </GridItem>
        <GridItem alignContent='center'>
         <FileName file={file} />
        </GridItem>
        <GridItem alignContent='center'>
         <Flex align='center' gap='8px'>
          <IconButton
           aria-label='Baixar evidência'
           icon={<FiDownload />}
           size='sm'
           variant='ghost'
           colorScheme='blue'
           onClick={() => handleDownload(file)}
           isDisabled={!file?.file_path}
          />
          <IconButton
           aria-label='Excluir evidência'
           icon={<FiTrash2 />}
           size='sm'
           variant='ghost'
           colorScheme='red'
           onClick={() => setDel({ file, step: 1 })}
           isDisabled={!canDelete}
          />
         </Flex>
        </GridItem>
       </Grid>
      );
     })}
    </ModalBody>
   </ModalContent>
  </Modal>

  <AttentionModal
   isOpen={!!del}
   onClose={() => !deleting && setDel(null)}
   submit={confirmDelete}
   isLoading={deleting}
   redType
   zIndex={12000}
   title={
    del?.step === 2
     ? 'Confirmar exclusão da evidência'
     : 'Excluir esta evidência?'
   }
   content={
    del?.step === 2
     ? `Confirma excluir definitivamente "${delName}"? Esta ação não pode ser desfeita. Esta é a segunda confirmação.`
     : 'O arquivo será removido permanentemente (S3 + banco). Deseja continuar?'
   }
  />
  </>
 );
}
